import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";

export const AdministratorMyShortedEventsDuringBlackoutPeriods = () => {
    const dispatch = useDispatch();

    const [myEvents, setMyEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [onlyShowBroadcastedEventsFilterValue, setOnlyShowBroadcastedEventsFilterValue] = useState(false);
    const [lastSelectedEventId, setLastSelectedEventId] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 20,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };

    const getMyEvents = (onlyShowBroadcastedEvents) => {
        setIsLoading(true);

        if (onlyShowBroadcastedEvents === undefined) {
            onlyShowBroadcastedEvents = onlyShowBroadcastedEventsFilterValue;
        }

        const dataItem = {
            onlyShowBroadcastedEvents: onlyShowBroadcastedEvents,
        };

        fetch(`/api/administrator/pos/myshortedeventsduringblackoutperiods`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setMyEvents(result);
                setTotalEventCount(result.length);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedEventId(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const getGridData = () => {
        return myEvents.slice(page.skip, page.take + page.skip);
    };

    const deleteEvents = (ids) => {
        if (window.confirm("Are you sure you want to delete these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: ids,
        };

        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted from POS successfully");

                getMyEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const deleteEvent = (id) => {
        if (window.confirm("Are you sure you want to delete this event?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: [id],
        };

        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Event deleted from POS successfully");

                getMyEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const selectedValueChanged = (event, id, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let myEventsCopy = myEvents;

            const itemIndex = myEventsCopy.findIndex(dataItem =>
                dataItem.id === id
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedEventId !== undefined
                && lastSelectedEventId !== id) {
                const lastSelectedItemIndex = myEventsCopy.findIndex(dataItem =>
                    dataItem.id === lastSelectedEventId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        myEventsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                myEventsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedEventId(id);
            }

            setMyEvents(myEventsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let myEventsCopy = myEvents;
            
            myEventsCopy.forEach(dataItem =>
                dataItem.selected = false
            );

            if (checked === true) {
                const gridData = getGridData();

                myEventsCopy.forEach(dataItem => {
                    const exists = gridData.includes(dataItem);

                    if (exists) {
                        dataItem.selected = true
                    }
                });
            }      

            setMyEvents(myEventsCopy);

            setLastSelectedEventId(undefined);

            setIsLoading(false);
        }, 1);
    };

    const deleteSelectedEvents = () => {
        let ids = [];

        myEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                ids.push(dataItem.id);
            }
        });

        if (ids.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        deleteEvents(ids);
    };

    const DeleteCell = (props) => {
        const id = props.dataItem.id;

        return (
            <td className="text-nowrap">
                <Button type={"button"} icon="trash" title="Delete" onClick={() => deleteEvent(id)}>
                    Delete
                </Button>
            </td>
        );
    };

    const SelectedCell = (props) => {
        const id = props.dataItem.id;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, id, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const onlyShowBroadcastedEventsFilterValueChanged = (event) => {
        const value = event.value;

        setOnlyShowBroadcastedEventsFilterValue(value);
        getMyEvents(value);
    }

    useEffect(() => {
        document.title = "My Shorted Events During Blackout Periods";

        getMyEvents();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></>}

            <div className="k-form k-form-md">
                <div className="k-form-fieldset container">
                    <div className="row">
                        <div className="col-12">
                            <div className="k-form-field">
                                <Checkbox
                                    label={"Only show broadcasted events"}
                                    onChange={onlyShowBroadcastedEventsFilterValueChanged}
                                    value={onlyShowBroadcastedEventsFilterValue}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="m-2">
                <Button type={"button"} icon="trash" title="Delete Selected Events" onClick={deleteSelectedEvents}>
                    Delete Selected Events
                </Button>
            </div> 

            <Grid
                data={getGridData()}
                resizable={true}
                skip={page.skip}
                take={page.take}
                total={totalEventCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column cell={DeleteCell} />
                <Column field="name" title={"Event"} />
                <Column field="eventDateAsString" title={"Date"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="venueCity" title={"City"} />
                <Column field="venueRegion" title={"Region"} />
                <Column field="venueCountryCode" title={"Country"} />
            </Grid>
        </div>
    );
}